import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, Cpu, Save, Trash2, Activity, CheckCircle2, AlertTriangle, LifeBuoy, KeyRound } from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import LlmHealthBanner from "@/components/LlmHealthBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  llmProviders, llmGlobalKeys, llmGlobalSlotSave, llmGlobalSlotDelete, llmGlobalSlotCheck, llmLegacyDelete,
} from "@/lib/api";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-419") : "—");
const KIND = { auth: "autenticación", quota: "saldo / cuota", rate_limit: "límite de peticiones (429)",
               timeout: "tiempo de espera / conexión", server: "error del proveedor (5xx)", invalid: "clave ilegible" };

/**
 * Fix 12: Inviktus has NO built-in LLM key. The admin stores a PRIMARY key (OpenRouter recommended)
 * and an optional BACKUP key (any provider) that answers when the primary fails.
 */
export default function AdminLlmKeys() {
  const [providers, setProviders] = useState([]);
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [pr, ks] = await Promise.all([llmProviders(), llmGlobalKeys()]);
      setProviders(pr.providers || []);
      setPanel(ks);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo cargar la configuración LLM."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <TopNav />
      <div className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
        <p className="eyebrow text-accent-blue flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Administración · Modelos LLM
        </p>
        <h1 className="mt-2 font-display text-4xl text-slate-900 sm:text-5xl">Claves LLM de Inviktus</h1>
        <p className="mt-3 max-w-3xl text-sm text-slate-600">
          Inviktus <strong>no trae ninguna clave incorporada</strong>. Estas claves las usan la entrevista ABC, el
          informe, la generación de agentes, los agentes de Inviktus y las demos, el curador de industria y el QA,
          salvo que un agente tenga su modelo propio. Recomendado: la <strong>clave principal de OpenRouter</strong>
          {" "}(un enrutador que da acceso a Claude, GPT, Gemini, DeepSeek, Kimi, Qwen…) y una <strong>clave de backup</strong>
          {" "}de otro proveedor. Si la principal falla por autenticación, saldo, cuota o una caída del proveedor (tras un
          reintento breve), responde la de backup. Los agentes de una empresa cliente <strong>no</strong> usan estas claves:
          funcionan con las de su empresa. Las claves se guardan cifradas y nunca se vuelven a mostrar.
        </p>

        <div className="mt-6 grid gap-3 sm:flex sm:items-center sm:gap-4 text-[11px] uppercase tracking-[0.18em] text-slate-500">
          <Link to="/admin/planes" className="hover:text-slate-900">Planes</Link>
          <span className="text-slate-300">·</span>
          <Link to="/admin/facturacion" className="hover:text-slate-900">Facturación</Link>
          <span className="text-slate-300">·</span>
          <Link to="/admin/kbl" className="hover:text-slate-900">Knowledge Base</Link>
          <span className="text-slate-300">·</span>
          <span className="inline-flex items-center gap-1 font-medium text-accent-blue"><Cpu className="h-3.5 w-3.5" /> Modelos LLM</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500" data-testid="llm-admin-loading">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            {panel?.status && !panel.status.configured && (
              <div className="border border-red-300 bg-red-50 p-4 text-sm text-red-800" data-testid="llm-admin-missing">
                <AlertTriangle className="mr-1.5 inline h-4 w-4" />
                <strong>Primera tarea:</strong> guarde la clave de OpenRouter como clave principal. Hasta entonces la
                entrevista, el informe y los agentes de Inviktus responden «No hay clave LLM configurada».
              </div>
            )}
            {panel?.status?.configured && <LlmHealthBanner status={panel.status} />}
            <div className="grid gap-6 lg:grid-cols-2">
              <SlotCard role="primary" title="Clave principal" icon={KeyRound} providers={providers}
                        slot={panel?.slots?.primary} inUse={panel?.status?.in_use === "primary"} onChanged={load} />
              <SlotCard role="backup" title="Clave de backup (opcional)" icon={LifeBuoy} providers={providers}
                        slot={panel?.slots?.backup} inUse={panel?.status?.in_use === "backup"} onChanged={load} />
            </div>
            {(panel?.legacy || []).length > 0 && (
              <section className="border border-slate-200 p-4" data-testid="llm-legacy">
                <p className="text-sm font-medium text-slate-800">Claves antiguas guardadas (no se usan)</p>
                <p className="mt-1 text-[12px] text-slate-500">
                  Antes había una clave por proveedor; la más reciente pasó a ser la principal. Estas otras ya no se usan.
                </p>
                <ul className="mt-3 space-y-2">
                  {panel.legacy.map((l) => (
                    <li key={l.provider} className="flex items-center justify-between gap-3 text-[13px]">
                      <span>{providers.find((p) => p.id === l.provider)?.label || l.provider} · <span className="font-mono">{l.api_key_masked}</span></span>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm border-red-300 text-[10px] uppercase tracking-[0.14em] text-red-700"
                              data-testid={`llm-legacy-delete-${l.provider}`}
                              onClick={async () => {
                                try { await llmLegacyDelete(l.provider); toast.success("Clave antigua eliminada."); load(); }
                                catch (e) { toast.error(errMsg(e, "No se pudo eliminar.")); }
                              }}>
                        <Trash2 className="mr-1 h-3 w-3" /> Eliminar
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SlotCard({ role, title, icon: Icon, providers, slot, inUse, onChanged }) {
  const featured = providers.filter((p) => p.featured);
  const others = providers.filter((p) => !p.featured);
  const initial = () => {
    const p = slot?.provider || (role === "primary" ? "openrouter" : "anthropic");
    return { provider: p, model: slot?.model || providers.find((x) => x.id === p)?.default_model || "",
             api_key: "", base_url: slot?.base_url || "" };
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState("");
  const [check, setCheck] = useState(slot?.last_checked || null);
  useEffect(() => { setForm(initial()); setCheck(slot?.last_checked || null); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
            [slot?.provider, slot?.model, slot?.api_key_last4, slot?.base_url, providers.length]);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const prov = providers.find((p) => p.id === form.provider);
  const h = slot?.health || {};
  const tid = `llm-slot-${role}`;

  const save = async () => {
    const key = form.api_key.trim();
    if (!slot && !key) { toast.error("Pegue la API key."); return; }
    if (slot && !key && form.provider !== slot.provider) { toast.error("Al cambiar de proveedor pegue también la API key."); return; }
    setBusy("save");
    try {
      const r = await llmGlobalSlotSave(role, { provider: form.provider, model: form.model.trim() || null,
                                              base_url: form.base_url || null, ...(key ? { api_key: key } : {}) });
      toast.success(`${title} guardada (cifrada).`);
      if (r?.warning) toast.warning(r.warning);
      upd("api_key", "");
      await onChanged();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo guardar."));
    } finally {
      setBusy("");
    }
  };
  const remove = async () => {
    if (!window.confirm(`¿Quitar la ${title.toLowerCase()}?`)) return;
    setBusy("del");
    try { await llmGlobalSlotDelete(role); toast.success("Clave quitada."); await onChanged(); }
    catch (e) { toast.error(errMsg(e, "No se pudo quitar.")); }
    finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("check");
    try { setCheck(await llmGlobalSlotCheck(role, {})); }
    catch (e) { setCheck({ ok: false, message: errMsg(e, "No se pudo probar.") }); }
    finally { setBusy(""); }
  };

  return (
    <article className="border border-slate-200 bg-white p-5" data-testid={tid}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center bg-slate-900 text-accent-blue-light"><Icon className="h-4 w-4" /></div>
          <div>
            <h3 className="font-display text-lg text-slate-900">{title}</h3>
            <p className="text-[12px] text-slate-500">
              {slot ? <>{providers.find((p) => p.id === slot.provider)?.label || slot.provider} · <span className="font-mono">{slot.model}</span> · •••• {slot.api_key_last4 || "????"}</> : "Sin clave guardada"}
            </p>
          </div>
        </div>
        {inUse && <span className="bg-emerald-50 px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-emerald-700" data-testid={`${tid}-inuse`}>En uso</span>}
      </div>

      {slot && (
        <div className="mt-3 space-y-1 text-[12px]" data-testid={`${tid}-health`}>
          {h.failing_since ? (
            <p className="text-red-700"><AlertTriangle className="mr-1 inline h-3.5 w-3.5" />Fallando desde {fmt(h.failing_since)}
              {h.last_error ? <> · {KIND[h.last_error.kind] || h.last_error.kind} ({fmt(h.last_error.at)}): {h.last_error.message}</> : null}</p>
          ) : h.last_ok_at ? (
            <p className="text-emerald-700"><CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />Última respuesta correcta: {fmt(h.last_ok_at)}</p>
          ) : (
            <p className="text-slate-500">Aún no se ha usado.</p>
          )}
        </div>
      )}

      <div className="mt-4 grid gap-3">
        <label className="block text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600">Proveedor
          <Select value={form.provider} onValueChange={(v) => setForm((f) => ({ ...f, provider: v, base_url: "",
                                                                                model: providers.find((x) => x.id === v)?.default_model || "" }))}>
            <SelectTrigger className="mt-1 h-9 rounded-sm border-slate-300 text-sm normal-case tracking-normal" data-testid={`${tid}-provider`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {featured.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              {others.length > 0 && <SelectItem value="__sep" disabled>── Otros proveedores ──</SelectItem>}
              {others.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
        <label className="block text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600">Modelo
          <Input value={form.model} onChange={(e) => upd("model", e.target.value)} list={`${tid}-models`}
                 className="mt-1 h-9 rounded-sm border-slate-300 font-mono text-sm normal-case tracking-normal" data-testid={`${tid}-model`} />
          <datalist id={`${tid}-models`}>{(prov?.models || []).map((m) => <option key={m} value={m} />)}</datalist>
          <span className="mt-1 block normal-case tracking-normal text-slate-400">Por defecto: {prov?.default_model}{form.provider === "openrouter" ? " (ids del tipo proveedor/modelo)" : ""}</span>
        </label>
        {(prov?.api_base_options || []).length > 0 && (
          <label className="block text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600">Región del endpoint
            <Select value={form.base_url || prov.api_base_options[0].url} onValueChange={(v) => upd("base_url", v)}>
              <SelectTrigger className="mt-1 h-9 rounded-sm border-slate-300 text-sm normal-case tracking-normal" data-testid={`${tid}-region`}><SelectValue /></SelectTrigger>
              <SelectContent>{prov.api_base_options.map((o) => <SelectItem key={o.url} value={o.url}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </label>
        )}
        <label className="block text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600">API key
          <Input type="password" autoComplete="new-password" value={form.api_key} onChange={(e) => upd("api_key", e.target.value)}
                 placeholder={slot ? "Deje vacío para conservar la actual" : (prov?.key_prefix ? `${prov.key_prefix}…` : "")}
                 className="mt-1 h-9 rounded-sm border-slate-300 font-mono text-sm normal-case tracking-normal" data-testid={`${tid}-apikey`} />
          {prov?.get_key_url && <a href={prov.get_key_url} target="_blank" rel="noreferrer" className="mt-1 block normal-case tracking-normal text-accent-blue underline">Obtener una clave de {prov.label}</a>}
        </label>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={!!busy} className="h-9 rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.16em] text-white" data-testid={`${tid}-save`}>
            {busy === "save" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />} Guardar
          </Button>
          {slot && (
            <Button variant="outline" onClick={test} disabled={!!busy} className="h-9 rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.16em]" data-testid={`${tid}-check`}>
              {busy === "check" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Activity className="mr-1 h-3.5 w-3.5" />} Probar clave
            </Button>
          )}
          {slot && (
            <Button variant="outline" onClick={remove} disabled={!!busy} className="h-9 rounded-sm border-red-300 text-[11px] uppercase tracking-[0.16em] text-red-700" data-testid={`${tid}-delete`}>
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Quitar
            </Button>
          )}
        </div>
        {check && (
          <p className={`text-[12px] ${check.ok ? "text-emerald-700" : "text-red-700"}`} data-testid={`${tid}-check-result`}>
            {check.ok ? `OK · ${check.model || ""}${check.latency_ms ? ` · ${check.latency_ms} ms` : ""}` : `Falla: ${check.message || check.kind || "error"}`}
            {check.at ? ` · ${fmt(check.at)}` : ""}
          </p>
        )}
      </div>
    </article>
  );
}
